import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './Layout.css';
import IconaServizio from './IconaServizio';
import AvvisoRiprova from './AvvisoRiprova';
import { serviziPerUtente, vociImpostazioni, servizioDelPercorso } from '../servizi';

// Struttura di ogni pagina interna: header, banda laterale con i servizi e Impostazioni,
// sotto-menu del servizio in cima al contenuto, footer. È solo navigazione: i permessi li controlla il server.
export default function Layout({ children, larga = false, piena = false }) {
  const { utente, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const studio = utente.ruolo === 'studio';

  const servizi = serviziPerUtente(utente);
  const attivo = servizioDelPercorso(location.pathname, utente);
  const inHome = studio && location.pathname === '/';
  const servizioAttivo = servizi.find(s => s.chiave === attivo);
  const voci = servizioAttivo?.voci || [];
  const vociDestra = voci.filter(m => m.destra);
  const archivi = voci.slice(1).filter(m => !m.destra);
  const contatoreUrl = servizioAttivo?.consentito ? vociDestra.find(m => m.contatore)?.contatore : null;

  // Numero di elementi nel Cestino del servizio: si aggiorna a ogni cambio pagina e quando una pagina avvisa di averlo cambiato
  const [conteggio, setConteggio] = useState(0);
  useEffect(() => {
    if (!contatoreUrl) return undefined;
    let annullato = false;
    const leggi = () => {
      Promise.resolve(api.get(contatoreUrl))
        .then(r => { if (!annullato && r?.data) setConteggio(Number(r.data.totale) || 0); })
        .catch(() => {});
    };
    leggi();
    window.addEventListener('cestino-aggiornato', leggi);
    return () => { annullato = true; window.removeEventListener('cestino-aggiornato', leggi); };
  }, [contatoreUrl, location.pathname]);

  const apri = (s) => {
    if (!s.cliccabile) return;
    navigate(s.consentito ? s.percorso : '/accesso-negato');
  };

  return (
    <div className="layout">
      <AvvisoRiprova />
      <header className="header">
        {/* Il logo riporta alla pagina iniziale (schede dei servizi per lo studio) */}
        <button type="button" className="headerLogo" onClick={() => navigate('/')} title="Pagina iniziale">
          <img src="/logo_boldrin_white.svg" alt="Studio Boldrin" className="logo" />
        </button>
        <div className="headerRight">
          <div className="utenteInfo">
            <span className="utenteNome">{utente.nome} {utente.cognome}</span>
            <span className="badge">{studio ? 'Studio' : utente.ragione_sociale || 'Azienda'}</span>
          </div>
          <button onClick={logout} className="esciBtn">Esci</button>
        </div>
      </header>

      <div className="corpo">
        <aside className="banda">
          <nav className="bandaNav" aria-label="Servizi">
            {studio && (
              <button
                type="button"
                onClick={() => navigate('/')}
                aria-current={inHome ? 'page' : undefined}
                className={`bandaVoce ${inHome ? 'bandaVoceAttiva' : ''}`}
              >
                <IconaServizio icona="casa" />
                <span className="bandaTesto">Home</span>
              </button>
            )}
            {servizi.map(s => (
              <button
                key={s.chiave}
                type="button"
                onClick={() => apri(s)}
                disabled={!s.cliccabile}
                aria-current={attivo === s.chiave ? 'page' : undefined}
                className={`bandaVoce ${attivo === s.chiave ? 'bandaVoceAttiva' : ''} ${s.cliccabile ? '' : 'bandaVoceInArrivo'}`}
                title={!s.consentito ? 'Non hai il permesso per questo servizio' : s.cliccabile ? undefined : 'In arrivo'}
              >
                <IconaServizio icona={s.icona} />
                <span className="bandaTesto">{s.nome}</span>
                {!s.cliccabile && <span className="bandaArrivo">in arrivo</span>}
                {s.cliccabile && !s.consentito && <IconaServizio icona="lucchetto" dimensione={15} className="bandaLucchetto" />}
              </button>
            ))}
          </nav>
          <MenuImpostazioni utente={utente} />
        </aside>

        <div className="contenuto">
          {voci.length > 1 && (
            <nav className="subNav" aria-label="Menu del servizio">
              {/* Pagina principale e archivi scorrono insieme; il Cestino (a destra) resta fuori dallo scorrimento */}
              <div className="subNavScorri">
              {/* La prima voce del servizio è la pagina principale; le altre sono gli archivi */}
              <button
                type="button"
                onClick={() => navigate(voci[0].path)}
                aria-current={location.pathname === voci[0].path ? 'page' : undefined}
                className={`navPrincipale ${location.pathname === voci[0].path ? 'navPrincipaleAttivo' : ''}`}
              >
                <IconaServizio icona="casa" dimensione={16} />
                <span>Pagina principale</span>
              </button>
              {archivi.length > 0 && <span className="navSeparatore" aria-hidden="true" />}
              {archivi.length > 0 && <span className="navArchivi">Archivi</span>}
              {archivi.map(m => (
                <button
                  key={m.path}
                  type="button"
                  onClick={() => navigate(m.path)}
                  aria-current={location.pathname === m.path ? 'page' : undefined}
                  className={`navBtn ${location.pathname === m.path ? 'navBtnActive' : ''}`}
                >
                  {m.label}
                </button>
              ))}
              </div>
              {vociDestra.map(m => (
                <button
                  key={m.path}
                  type="button"
                  onClick={() => navigate(m.path)}
                  aria-current={location.pathname === m.path ? 'page' : undefined}
                  className={`navDestra navCestino ${location.pathname === m.path ? 'navDestraAttiva' : ''}`}
                  aria-label={m.contatore && conteggio > 0 ? `${m.label} (${conteggio})` : m.label}
                >
                  {m.icona && <IconaServizio icona={m.icona} dimensione={16} />}
                  <span>{m.label}</span>
                  {m.contatore && conteggio > 0 && <span className="navContatore" aria-hidden="true">{conteggio}</span>}
                </button>
              ))}
            </nav>
          )}
          <main className={`main ${larga ? 'mainLarga' : ''} ${piena ? 'mainPiena' : ''}`}>{children}</main>
        </div>
      </div>

      <footer className="footer">
        <span>Studio Boldrin | Dottori Commercialisti - Revisori Legali - Avvocati</span>
      </footer>
    </div>
  );
}

// Ingranaggio in fondo alla banda: Cambia password per tutti, Utenti e permessi e Cestino per l'admin
function MenuImpostazioni({ utente }) {
  const [aperto, setAperto] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);
  const voci = vociImpostazioni(utente);

  useEffect(() => {
    if (!aperto) return undefined;
    const chiudi = (e) => { if (box.current && !box.current.contains(e.target)) setAperto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAperto(false); };
    document.addEventListener('mousedown', chiudi);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', chiudi); document.removeEventListener('keydown', esc); };
  }, [aperto]);

  const generali = voci.filter(v => !v.soloAdmin);
  const admin = voci.filter(v => v.soloAdmin);
  const vai = (path) => { setAperto(false); navigate(path); };

  return (
    <div className="impostazioni" ref={box}>
      {aperto && (
        <div className="impMenu" role="menu">
          <div className="impTag">Impostazioni</div>
          {generali.map(v => <button key={v.chiave} type="button" role="menuitem" onClick={() => vai(v.path)}>{v.label}</button>)}
          {admin.length > 0 && (
            <>
              <hr />
              <div className="impTag">Solo admin</div>
              {admin.map(v => <button key={v.chiave} type="button" role="menuitem" onClick={() => vai(v.path)}>{v.label}</button>)}
            </>
          )}
        </div>
      )}
      <button type="button" className="impBtn" onClick={() => setAperto(a => !a)} aria-expanded={aperto} aria-haspopup="menu" aria-label="Impostazioni">
        <IconaServizio icona="ingranaggio" />
        <span className="bandaTesto">Impostazioni</span>
      </button>
    </div>
  );
}
